/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
import { A } from '@ember/array';
import EmberObject, { computed, set } from '@ember/object';

import * as ArrayUtils from '../utils/array';
import MathUtils from '../utils/math';
import * as StageUtils from '../utils/stage';
import * as CommonUtils from '../../utils/common';
import * as ConversionUtils from '../utils/conversion';
import SecondOrderStage from '../stages/second-order';
import FirstOrderStage from '../stages/first-order';
import FilterConstants from '../../filter-constants';
import StageDetails from './sub-results/stage-details';
import * as ResultsUtils from '../utils/results';

/**
 * Should contain data that will be used for output or computing data for the next step in the wizard.
 */
export default class SpecificationResults extends EmberObject {
  specificationsInput;
  filter;
  init() {
    var filter = this.filter;
    var specificationsInput = this.specificationsInput;
    if (CommonUtils.isNullOrUndefined(this.friendlyFrequencyArray)) {
      var frequencyArrayValue = filter.getFilterFrequencyRange(
        specificationsInput.stopbandFrequency,
        specificationsInput.passbandFrequency,
        specificationsInput.centerFrequency,
      ).friendlyFrequencyArray;
      set(this, 'friendlyFrequencyArray', frequencyArrayValue);
    }

    if (CommonUtils.isNullOrUndefined(this.timeAxis)) {
      var timeAxisValue = filter.createTimeAxis(
        specificationsInput.passbandFrequency,
        specificationsInput.stopbandFrequency,
      );
      set(this, 'timeAxis', timeAxisValue);
    }

    this.responseType = this.specificationsInput.selectedResponseType;
    this.filterResponseValue = this.specificationsInput.filterResponseValue;
  }

  @computed('filter', 'specificationsInput') get timeAxis() {
    var filter = this.filter;
    var specifications = this.specificationsInput;
    var result = filter.createTimeAxis(specifications.passbandFrequency, specifications.stopbandFrequency);
    return result;
  }

  @computed('filter', 'specificationsInput') get filterFrequencyRange() {
    var filter = this.filter;
    var specificationsInput = this.specificationsInput;
    var result = filter.getFilterFrequencyRange(
      specificationsInput.stopbandFrequency,
      specificationsInput.passbandFrequency,
      specificationsInput.centerFrequency,
    );
    return result;
  }
  @computed('filterFrequencyRange.narrowFrequencyArray') get frequencyArray() {
    return this.filterFrequencyRange.narrowFrequencyArray;
  }
  @computed('filterFrequencyRange.friendlyFrequencyArray') get friendlyFrequencyArray() {
    return this.filterFrequencyRange.friendlyFrequencyArray;
  }
  @computed('filter.name') get filterName() {
    return this.filter.name;
  }

  @computed('specificationsInput.{gain,gainUnit}') get targetGain() {
    var gainUnit = this.specificationsInput.gainUnit;
    var gain = this.specificationsInput.gain;
    var result = gain;
    if (gainUnit === FilterConstants.gainUnits.db) {
      result = ConversionUtils.dBtoVV(gain);
    }

    return result;
  }

  @computed('specificationsInput', 'filter', 'filterFrequencyRange', 'timeAxis') get _targetStages() {
    var filter = this.filter;
    var specificationsInput = this.specificationsInput;
    var filterFrequencyRange = this.filterFrequencyRange;
    var timeAxis = this.timeAxis;
    var selectedResponseType = specificationsInput.selectedResponseType;
    set(filter, 'selectedResponseType', selectedResponseType);

    var stages = filter.getStages({
      filterResponseType: selectedResponseType,
      passbandFrequency: specificationsInput.passbandFrequency,
      passbandAttenuation: specificationsInput.passbandAttenuation,
      stopbandFrequency: specificationsInput.stopbandFrequency,
      stopbandAttenuation: specificationsInput.stopbandAttenuation,
      centerFrequency: specificationsInput.centerFrequency,
      filterResponseValue: specificationsInput.filterResponseValue,
    });
    // var standardComponentFrequencies = stages.filter((item) => { return !item.isHighSpeedHighGainStage; }).map((item) => { return item.stageFp; });
    var mappedStages = stages.map((stage, index) => {
      if (stage.filter === null || stage.filter === undefined) {
        set(stage, 'filter', filter);
      }

      // if(!stage.isHighSpeedHighGainStage')) {
      //     stage.set('noiseResistanceFrequencyArray', standardComponentFrequencies);
      // }

      set(stage, 'filterFrequencyRange', filterFrequencyRange);
      set(stage, 'index', index);
      set(stage, 'timeAxis', timeAxis);
      set(stage, 'stageLetter', FilterConstants.stageLetter[index]);

      return stage;
    });

    mappedStages = A(mappedStages).sortBy('stageId');
    var result = StageUtils.setPreviousAndNextStage(mappedStages);

    return result;
  }

  @computed('_targetStages', 'specificationsInput.centerFrequency', 'targetGain')
  get targetStagesWithDistributedGain() {
    var stages = this._targetStages;
    var targetGain = this.targetGain;
    var centerFrequency = this.specificationsInput.centerFrequency;
    var result = StageUtils.distributeGains(targetGain, stages, centerFrequency);
    return result;
  }

  @computed('targetStagesWithDistributedGain') get totalGain() {
    var gainArray = this.targetStagesWithDistributedGain.map((item) => {
      return item.gain;
    });
    var result = 1;
    for (var i = 0; i < gainArray.length; i++) {
      result *= gainArray[i];
    }

    return result;
  }

  @computed('_targetStages') get nthOrder() {
    var stages = this._targetStages;
    var secondOrderStagesLength = stages.filter((stage) => {
      return stage instanceof SecondOrderStage;
    }).length;
    var firstOrderStageLength = stages.filter((stage) => {
      return stage instanceof FirstOrderStage;
    }).length;
    var result = secondOrderStagesLength * 2 + firstOrderStageLength;

    return result;
  }

  @computed('_targetStages.length') get numberOfStages() {
    var result = this._targetStages.length;
    return result;
  }

  @computed('targetStagesWithDistributedGain') get _magnitude() {
    var stagesArray = this.targetStagesWithDistributedGain;
    var individualStageMagnitude = stagesArray.map((stage) => {
      return stage.magnitude;
    });

    var result = ResultsUtils.getMagnitudeArray(individualStageMagnitude);
    return result;
  }

  @computed('_magnitude', 'friendlyFrequencyArray', 'specificationsInput') get magnitudeDb() {
    var magnitude = this._magnitude;
    var frequencyArray = this.friendlyFrequencyArray;
    var dbData = ConversionUtils.VVarrtoDBarr(magnitude) || [];
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, dbData);
    var limits = ResultsUtils.getRangeLimits(dataPoints);

    var specInputs = this.specificationsInput;
    var gainDB =
      specInputs.gainUnit === FilterConstants.gainUnits.db ? specInputs.gain : ConversionUtils.VVtoDB(specInputs.gain);
    limits.ymin = gainDB + (specInputs.stopbandAttenuation - 20);
    limits.ymax = gainDB + 10;

    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('_magnitude', 'friendlyFrequencyArray') get magnitudeVV() {
    var frequencyArray = this.friendlyFrequencyArray;
    var magnitude = this._magnitude;
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, magnitude);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    limits.ymax += 0.2; // give 0.2 V/V headroom
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('targetStagesWithDistributedGain') get _phase() {
    var stagesArray = this.targetStagesWithDistributedGain;
    var individualStagePhase = stagesArray.map((stage) => {
      return stage.phase;
    });

    var result = ResultsUtils.getPhaseArray(individualStagePhase);
    return result;
  }

  @computed('_phase', 'friendlyFrequencyArray') get phaseDegrees() {
    var frequencyArray = this.friendlyFrequencyArray;
    var phaseArray = this._phase;
    var dbData = ConversionUtils.radianArrayToDegreeArray(phaseArray);
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, dbData);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var newYmax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var newYmin = limits.ymin - (limits.ymax - limits.ymin) / 10;
    limits.ymax = newYmax;
    limits.ymin = newYmin;
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('_phase', 'friendlyFrequencyArray') get phaseRadians() {
    var frequencyArray = this.friendlyFrequencyArray;
    var phaseArray = this._phase;
    var dataPoints = ArrayUtils.joinArrays(frequencyArray, phaseArray);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var result = { data: dataPoints, limits: limits };
    var newYmax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var newYmin = limits.ymin - (limits.ymax - limits.ymin) / 10;
    limits.ymax = newYmax;
    limits.ymin = newYmin;
    return result;
  }

  @computed('_phase', 'friendlyFrequencyArray') get groupDelay() {
    var phaseArray = this._phase;
    var narrowFreqArray = this.friendlyFrequencyArray;
    var { groupDelayArray, newFrequencyAxis } = ConversionUtils.groupDelay(phaseArray, narrowFreqArray);
    var dataPoints = ArrayUtils.joinArrays(newFrequencyAxis, groupDelayArray);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    limits.ymax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('_phase', 'friendlyFrequencyArray') get phaseDelay() {
    var phaseArray = this._phase;
    var narrowFreqArray = this.friendlyFrequencyArray;
    var dbData = ConversionUtils.phaseDelay(phaseArray, narrowFreqArray);
    var dataPoints = ArrayUtils.joinArrays(narrowFreqArray, dbData);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    limits.ymax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('targetStagesWithDistributedGain') get _stepResponse() {
    var stages = this.targetStagesWithDistributedGain;
    var tempArray1 = stages[0].stepResponseIdeal;
    var tempArray2 = [];
    for (var i = 1; i < stages.length; i++) {
      var stage = stages[i];
      var impulseRes = stage.impulseResponse;
      tempArray2 = MathUtils.convolve(tempArray1, impulseRes);
      tempArray1 = tempArray2;
    }

    return tempArray1;
  }

  @computed('_stepResponse', 'timeAxis') get stepResponse() {
    var stepResponse = this._stepResponse;
    var timeAxis = this.timeAxis;
    var dataPoints = ArrayUtils.joinArrays(timeAxis, stepResponse);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var overreach = (limits.ymax - limits.ymin) / 10;
    limits.ymax = limits.ymax + overreach;
    if (limits.ymin < 0) {
      limits.ymin = limits.ymin - overreach;
    }
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('targetStagesWithDistributedGain', 'magnitudeDb.limits') get detailsOfStages() {
    var limMagnitudeDb = this.magnitudeDb.limits;
    var targetStages = this.targetStagesWithDistributedGain;
    var result = targetStages.map((targetStage) => {
      var graphData = ResultsUtils.getGraphData(targetStage);
      graphData.limits = limMagnitudeDb;
      var res = StageDetails.create({ targetStage: targetStage, graphData: graphData });
      return res;
    });

    return result;
  }
}
